import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Field, Form, Formik } from "formik";
import { DateTime } from "luxon";
import { ChangeEvent, useState } from "react";

import {
  Case,
  CaseAdmin,
  Patient,
  PatientFullInsurance,
  PatientInsurance,
  UserContact,
} from "pwapi";

import useClinicFields, { isMRNRequired } from "shared/hooks/useClinicFields";
import {
  patientAdminSchema,
  patientWarningSchema,
} from "shared/utils/caseSchema";
import {
  canValidate,
  getErrors,
  getValidationWarnings,
  getValueFromObject,
} from "shared/utils/validationUtils";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import CustomInput from "components/customInputs/CustomInput";
import DateInput from "components/customInputs/DateInput";
import SelectInput from "components/customInputs/SelectInput";
import TextInput from "components/customInputs/TextInput";

import FormErrors from "./FormErrors";
import FormikContext from "./FormikContext";
import GuarantorLine from "./GuarantorLine";
import InsuranceLine from "./InsuranceLine";
import InsuranceWarningDialog from "./InsuranceWarningDialog";
import LocationLine from "./LocationLine";

type PatientFormProps = {
  accessionCompleted?: string | null;
  patient: PatientFullInsurance;
  orderingDoctor?: UserContact;
  setValues: Function;
  onSubmit: any;
  setEdited: Function;
  setEditLocation: Function;
  setEditInsurance: Function;
  setEditGuarantor: Function;
  caseSource: CaseAdmin.source;
  canUpdateCase: boolean;
  caseType?: Case.type;
};

const PatientForm = ({
  accessionCompleted,
  patient,
  orderingDoctor,
  setValues,
  onSubmit,
  setEdited,
  setEditLocation,
  setEditInsurance,
  setEditGuarantor,
  caseSource,
  canUpdateCase,
  caseType,
}: PatientFormProps) => {
  const [delInsurance, setDelInsurance] = useState<PatientInsurance | null>(
    null
  );
  const [warnings, setWarnings] = useState({});

  const primaryInsurance = patient.insurance?.find(
    (insur) => insur.primary_provider
  );
  const otherInsurance = patient.insurance?.filter(
    (insur) => !insur.primary_provider
  );
  const requireMRN =
    caseType === Case.type.CONSULT
      ? false
      : isMRNRequired(
          useClinicFields({
            clinic: patient?.clinic,
            ordering_doctor_id: orderingDoctor?.id,
          })
        );
  const canValidateForm = canValidate(patient.clinic?.customer_type);

  return (
    <>
      {delInsurance && (
        <InsuranceWarningDialog
          insurance={delInsurance}
          onClose={() => {
            setDelInsurance(null);
          }}
        />
      )}
      <Formik
        initialValues={{ ...patient, caseType, orderingDoctor }}
        enableReinitialize
        onSubmit={onSubmit}
        validate={(values: any) => {
          canValidateForm &&
            patientWarningSchema
              .validate(values, {
                abortEarly: false,
              })
              .then(() => {
                setWarnings({});
              })
              .catch((err) => {
                setWarnings(getValidationWarnings(err));
              });
        }}
        validateOnBlur={false}
        validationSchema={canValidateForm ? patientAdminSchema : null}
        validateOnMount={true}
      >
        {({ resetForm, dirty, values, errors, touched }) => {
          const allErrors = getErrors(errors, touched, !accessionCompleted);
          const allWarnings = getErrors(warnings, touched, !accessionCompleted);

          return (
            <Form className="AdminPanelForm panelContainer">
              <FormikContext setValues={setValues} setEdited={setEdited} />
              <fieldset className="panelContainer__section">
                <legend className="panelContainer__section--title">
                  Patient Name
                </legend>
                <div className="panelContainer__inputGroup">
                  <Field name="last_name">
                    {({ field }: any) => (
                      <TextInput
                        label="Last Name"
                        id="last_name"
                        {...field}
                        disabled={!canUpdateCase}
                      />
                    )}
                  </Field>
                  <Field name="first_name">
                    {({ field }: any) => (
                      <TextInput
                        label="First Name"
                        id="first_name"
                        {...field}
                        disabled={!canUpdateCase}
                      />
                    )}
                  </Field>
                  {caseSource === CaseAdmin.source.PW && (
                    <Field name="middle_initial">
                      {({ field }: any) => (
                        <TextInput
                          label="M.I."
                          id="middle_initial"
                          maxLength={2}
                          {...field}
                          disabled={!canUpdateCase}
                        />
                      )}
                    </Field>
                  )}
                </div>
              </fieldset>

              <fieldset className="panelContainer__section">
                <legend className="panelContainer__section--title">
                  Patient Details
                </legend>
                <div className="panelContainer__inputGroup">
                  {caseSource === CaseAdmin.source.PW && (
                    <Field name="ssn">
                      {({ field }: any) => (
                        <TextInput
                          label="SSN"
                          id="ssn"
                          min="0"
                          {...field}
                          disabled={!canUpdateCase}
                          showErrorText={false}
                          error={getValueFromObject(allErrors, "ssn")}
                        />
                      )}
                    </Field>
                  )}
                  <Field name="date_of_birth">
                    {({ field, form }: any) => (
                      <CustomInput
                        label="Date of birth"
                        id="dob"
                        showErrorText={false}
                        error={getValueFromObject(allErrors, "date_of_birth")}
                        warning={getValueFromObject(
                          allWarnings,
                          "date_of_birth"
                        )}
                      >
                        <DateInput
                          {...field}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            form.setFieldValue("date_of_birth", e.target.value);
                          }}
                          disabled={!canUpdateCase}
                          max={DateTime.now().toISODate()}
                        />
                      </CustomInput>
                    )}
                  </Field>
                  <Field name="sex">
                    {({ field }: any) => (
                      <div style={{ width: "4em", flex: "0 0 auto" }}>
                        <SelectInput
                          id="sex"
                          label="Sex"
                          {...field}
                          options={Object.values(Patient.sex).map((s) => ({
                            label: s,
                            value: s,
                          }))}
                          disabled={!canUpdateCase}
                        />
                      </div>
                    )}
                  </Field>
                </div>
                {caseSource === CaseAdmin.source.PW && (
                  <Field name="medical_record_number">
                    {({ field }: any) => (
                      <TextInput
                        label={`Medical Record Number${requireMRN ? " *" : ""}`}
                        id="medical_record_number"
                        {...field}
                        disabled={!canUpdateCase}
                        required={requireMRN}
                        error={getValueFromObject(
                          allErrors,
                          "medical_record_number"
                        )}
                      />
                    )}
                  </Field>
                )}
              </fieldset>
              {caseSource === CaseAdmin.source.PW && (
                <fieldset className="panelContainer__section">
                  <legend className="panelContainer__section--title">
                    Patient Address
                  </legend>
                  <LocationLine
                    patientLocation={values.address}
                    onEditLocation={(loc: any) => {
                      setEditLocation({
                        resource: "Patient",
                        location: loc,
                      });
                    }}
                  />
                </fieldset>
              )}
              {caseSource === CaseAdmin.source.PW ? (
                <fieldset className="panelContainer__section">
                  <legend className="panelContainer__section--title">
                    Patient Guarantor
                  </legend>
                  {!!patient.guarantor && patient.guarantor.length > 0 ? (
                    <GuarantorLine
                      guarantor={patient.guarantor[0]}
                      onEditGuarantor={setEditGuarantor}
                    />
                  ) : (
                    canUpdateCase && (
                      <PathologyWatchButton
                        onClick={() => setEditGuarantor({})}
                      >
                        Add Patient Guarantor
                      </PathologyWatchButton>
                    )
                  )}
                </fieldset>
              ) : undefined}
              {!!patient.insurance && (
                <fieldset className="panelContainer__section">
                  <legend className="panelContainer__section--title">
                    Insurance Information
                  </legend>
                  {(!!primaryInsurance || !!otherInsurance?.length) && (
                    <CustomInput label="Primary">
                      {primaryInsurance ? (
                        <InsuranceLine
                          insurance={primaryInsurance}
                          onEditInsurance={
                            caseSource === CaseAdmin.source.PW
                              ? setEditInsurance
                              : undefined
                          }
                          onDeleteInsurance={
                            caseSource === CaseAdmin.source.PW && canUpdateCase
                              ? setDelInsurance
                              : undefined
                          }
                        />
                      ) : (
                        <span style={{ color: "var(--imperialRed)" }}>
                          <FontAwesomeIcon icon="exclamation-circle" /> Missing
                          Primary Insurance
                        </span>
                      )}
                    </CustomInput>
                  )}
                  {!!otherInsurance?.length && (
                    <CustomInput label="Additional">
                      <div className="insuranceList">
                        {otherInsurance.map((insur, index) => (
                          <InsuranceLine
                            key={`${insur.id}-${index}`}
                            insurance={insur}
                            onEditInsurance={
                              caseSource === CaseAdmin.source.PW
                                ? setEditInsurance
                                : undefined
                            }
                            onDeleteInsurance={
                              caseSource === CaseAdmin.source.PW &&
                              canUpdateCase
                                ? setDelInsurance
                                : undefined
                            }
                          />
                        ))}
                      </div>
                    </CustomInput>
                  )}
                  {caseSource === CaseAdmin.source.PW && canUpdateCase ? (
                    <PathologyWatchButton
                      onClick={() => {
                        setEditInsurance({ primary_provider: false });
                      }}
                    >
                      Add Insurance +
                    </PathologyWatchButton>
                  ) : (
                    <></>
                  )}
                </fieldset>
              )}

              <FormErrors errors={allErrors} warnings={allWarnings} />
              <div className="form__actionContainer">
                <PathologyWatchButton
                  title="Discard changes to patient"
                  color="red"
                  onClick={() => {
                    resetForm();
                  }}
                  disabled={!dirty}
                >
                  Discard Changes
                </PathologyWatchButton>
                <PathologyWatchButton
                  type="submit"
                  title="Save patient"
                  color="green"
                  disabled={
                    !dirty ||
                    !canUpdateCase ||
                    Object.keys(allErrors).length > 0
                  }
                >
                  Save Changes
                </PathologyWatchButton>
              </div>
            </Form>
          );
        }}
      </Formik>
    </>
  );
};

export default PatientForm;
